/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Page Account Info Component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.3
 * @since 1.10.0
 */

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import roamingIcon from '@megafon/ui-icons/basic-16-roaming_16.svg';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { EFastActionsButtonTypes } from '@/main/components/FastActions/constants/constants';
import { FastActionButton } from '@/main/components/FastActions/FastActionButton';
import { getRoamSubsText, openRoamSubscribersTable } from '@/main/components/FastActions/fastActionsButtonsUtils';
import { VerticalSeparateLineStyled } from '@/main/components/FastActions/styled/FastActionsPlateStyled';
import { icons } from '@assets/index';
import { LinkBlock, PageTitle, SideMenu, SideMenuItem } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import type { ISideMenuItem } from '@components/common/SideMenu/SideMenuItem';
import Title from '@components/common/Title/Title';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectPageAccountInfoTranslation,
    makeSelectTranslationCommon,
    makeSelectTranslationFastActions,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { Loader } from '@modules/loader/components/Loader';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import NoticeMessage from '@modules/notification/components/NoticeMessage';
import { ENotificationPlace } from '@modules/notification/types/notificationTypes';
import { AccessComponentKeyEnum, FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { CLIENT_ID, RoutePath } from '@routes/constants/routesConstants';
import type { IRouteParams } from '@routes/constants/routesConstants';
import { icons as themeIcons } from '@theme/index';
import { formatDate } from '@utils/formatText';
import { stringFormat } from '@utils/stringFormat';

import ErrorPage from '../../ErrorPage/ErrorPage';
import { makeSelectFeatureEnabled } from '../../PageAuth/selectors/authSelectors';
import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { accountInfoApi } from '../store';
import { EPageAccountInfo } from '../types/pageAccountInfoTypes';
import PageAccountInfoFooter from './PageAccountInfoFooter';
import {
    PageAccountInfoBodyStyled,
    PageAccountInfoContentStyled,
    PageAccountInfoSideStyled,
    PageAccountInfoStyled,
    PageAccountInfoSyncDateStyled,
    PageAccountDateContainderStyled,
} from './styled/PageAccountInfoStyled';

interface IPageAccountInfoTab extends Pick<ISideMenuItem, 'id' | 'title'> {
    id: EPageAccountInfo;
    children?: IPageAccountInfoTab[];
    externalUrl: string;
    url: string;
}

const PageAccountInfo = ({ children }: { children: ReactNode }) => {

    const dispatch = useDispatch();

    const [tabs, setTabs] = useState<IPageAccountInfoTab[]>([]);
    const [tabsFeaturesEnabled, setTabsFeaturesEnabled] = useState<{ [tabId: string]: boolean }>({});
    const location = useLocation();
    const { clientId } = useParams<IRouteParams>();

    const isAccountInfoFinanceEnabled = useSelector(makeSelectGuardComponentEnabled(
        AccessComponentKeyEnum.PageAccountInfoFinances));
    const isAccountInfoGeneralEnabled = useSelector(makeSelectGuardComponentEnabled(
        AccessComponentKeyEnum.PageAccountInfoGeneral));
    const isAccountInfoShippingEnabled = useSelector(makeSelectGuardComponentEnabled(
        AccessComponentKeyEnum.PageAccountInfoShipping));
    const isSubsInRoamingEnabled = 
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.SUBSCRIBERS_IN_ROAMING_NOTIFICATION));

    const translation = useSelector(makeSelectPageAccountInfoTranslation());
    const translationCommon = useSelector(makeSelectTranslationCommon());
    const translationFastActions = 
        useSelector(makeSelectTranslationFastActions());

    const [initMetaLoad, {
        data: generalMeta,
        error,
        isError,
        isFetching,
        isSuccess,
    }] = accountInfoApi.endpoints.getAccountInfoMeta.useLazyQuery();

    const [loadMultiBalanceList, {
        data: multiBalancesList,
    }] = accountInfoApi.endpoints.getAccountInfoMultiBalanceList.useLazyQuery();

    const [getExhaustedList, {
        data: multiBalanceExhaustedList,
    }] = accountInfoApi.endpoints.getAccountInfoMultiBalanceExhaustedList.useLazyQuery();

    const subscribersInRoamingCount = generalMeta?.subscribersInRoamingCount ?? 0;
    const roamSubsText = 
        useMemo(() => getRoamSubsText(subscribersInRoamingCount, translationFastActions),
        [translationFastActions, subscribersInRoamingCount]);
    const isSubsInRoamingButtonShown = !!subscribersInRoamingCount && isSubsInRoamingEnabled;

    const loadInitData = useCallback(() => {
        if (clientId) {
            initMetaLoad({ clientId });
            getExhaustedList({ clientId });
            loadMultiBalanceList({ clientId });
        }
    }, [
        clientId,
        initMetaLoad,
        getExhaustedList,
        loadMultiBalanceList,
    ]);

    // При загрузке карточки всегда нужно запрашивать мету
    useEffect(() => {
        loadInitData();
    }, [loadInitData]);

    useEffect(() => {
        dispatch(logBusinessEvent({
            eventName: ELogBusinessEventName.B2B_ACCOUNT,
            type: ELogBusinessEventType.common,
        }));

        return () => {
            dispatch(accountInfoApi.util.resetApiState());
        };
    }, [dispatch]);

    /*
    * -------------------------------------------
    *         Render Side menu items
    * -------------------------------------------
    */

    const renderSideMenuItem = useCallback((item: IPageAccountInfoTab, level = 0) => (
        <LinkBlock
            isExternalUrl={!tabsFeaturesEnabled[item.id]}
            key={item.id}
            url={tabsFeaturesEnabled[item.id] ? item.url : item.externalUrl}
        >
            <SideMenuItem
                id={item.id}
                isActive={item.url === location.pathname}
                isOpen={item.children && location.pathname.includes(item.id.toString())}
                level={level}
                title={item.title}
            >
                {item.children?.map((children: IPageAccountInfoTab) => renderSideMenuItem(children, level + 1))}
            </SideMenuItem>
        </LinkBlock>
    ), [
        location.pathname,
        tabsFeaturesEnabled,
    ]);

    /*
    * -------------------------------------------
    *         Set tabs configuration
    * -------------------------------------------
    */

    useEffect(() => {
        setTabsFeaturesEnabled({
            [EPageAccountInfo.GENERAL]: isAccountInfoGeneralEnabled,
            [EPageAccountInfo.FINANCIAL]: isAccountInfoFinanceEnabled,
            [EPageAccountInfo.FINANCIAL_GENERAL]: isAccountInfoFinanceEnabled,
            [EPageAccountInfo.FINANCIAL_DISCOUNTS]: isAccountInfoFinanceEnabled,
            [EPageAccountInfo.FINANCIAL_MULTI_BALANCES]: isAccountInfoFinanceEnabled,
            [EPageAccountInfo.SHIPPING_ACCS]: isAccountInfoShippingEnabled,
        });
    }, [
        isAccountInfoFinanceEnabled,
        isAccountInfoGeneralEnabled,
        isAccountInfoShippingEnabled,
    ]);

    useEffect(() => {
        const tabs = [
            {
                externalUrl: `${RoutePath.accountInfoExternal.replace(CLIENT_ID, clientId)}#general_info`,
                id: EPageAccountInfo.GENERAL,
                title: translation.tabs.generalInfo,
                url: RoutePath.accountInfoGeneral.replace(CLIENT_ID, clientId),
            },
            {
                externalUrl: `${RoutePath.accountInfoExternal.replace(CLIENT_ID, clientId)}#financial_info`,
                id: EPageAccountInfo.FINANCIAL,
                title: translation.tabs.financialInfo,
                url: RoutePath.accountInfoFinancial.replace(CLIENT_ID, clientId),
                children: isAccountInfoFinanceEnabled ? [
                    {
                        externalUrl: `${RoutePath.accountInfoExternal.replace(CLIENT_ID, clientId)}#financial_info`,
                        id: EPageAccountInfo.FINANCIAL_GENERAL,
                        title: translation.tabs.financialInfoGeneral,
                        url: RoutePath.accountInfoFinancialGeneral.replace(CLIENT_ID, clientId),
                    },
                    {
                        externalUrl: `${RoutePath.accountInfoExternal.replace(CLIENT_ID, clientId)}#financial_info`,
                        id: EPageAccountInfo.FINANCIAL_DISCOUNTS,
                        title: translation.tabs.financialInfoDiscounts,
                        url: RoutePath.accountInfoFinancialDiscounts.replace(CLIENT_ID, clientId),
                    },
                ] : undefined,
            },
        ];
        if (isAccountInfoFinanceEnabled
            && multiBalancesList?.multiBalances.length
            && multiBalanceExhaustedList?.multiBalances.length
            && tabs[1].children
        ) {
            tabs[1].children.push(
                {
                    externalUrl: `${RoutePath.accountInfoExternal.replace(CLIENT_ID, clientId)}#financial_info`,
                    id: EPageAccountInfo.FINANCIAL_MULTI_BALANCES,
                    title: translation.tabs.financialInfoMultiBalances,
                    url: RoutePath.accountInfoFinancialMultiBalances.replace(CLIENT_ID, clientId),
                },
            );
        }
        if (generalMeta?.accountDeliveryPageInfo.isDeliveryTabShown) {
            tabs.push({
                externalUrl: `${RoutePath.accountInfoExternal.replace(CLIENT_ID, clientId)}#shipping_accs`,
                id: EPageAccountInfo.SHIPPING_ACCS,
                title: translation.tabs.shippingAccs,
                url: RoutePath.accountInfoAccs.replace(CLIENT_ID, clientId),
            });
        }
        setTabs(tabs);
    }, [
        clientId,
        generalMeta,
        isAccountInfoFinanceEnabled,
        multiBalancesList,
        multiBalanceExhaustedList,
        translation,
    ]);

    /*
    * -------------------------------------------
    *               Render
    * -------------------------------------------
    */

    return <PageAccountInfoStyled>

        <PageLocale lazy
                    fileNames={[
                        TranslationFileNamesEnum.common,
                        TranslationFileNamesEnum.corporate,
                        TranslationFileNamesEnum.pageAccountInfo,
                        TranslationFileNamesEnum.pagePersonalAccount,
                        TranslationFileNamesEnum.fastActions,
                    ]}/>

        <Loader showLoader={isFetching}>
            <Title pageName={ECommonTranslationPages.account}/>

            <PageTitle
                dataName={'pageAccountInfo'}
                title={stringFormat(translation.title, generalMeta?.accountNumber ?? '')}
            >
                <PageAccountInfoSyncDateStyled>
                    {   
                        isSubsInRoamingButtonShown && 
                        <>
                            <FastActionButton 
                                buttonIcon={roamingIcon} 
                                buttonText={roamSubsText}
                                buttonType={EFastActionsButtonTypes.SUBSCRIBERS_IN_ROAMING}
                                dataName="subscribers-in-roaming-button"
                                disabled={!subscribersInRoamingCount}
                                onClick={() => openRoamSubscribersTable(dispatch)}
                            />
                            <VerticalSeparateLineStyled/>
                        </>
                    }
                    <PageAccountDateContainderStyled>
                        {formatDate(moment().format(EFormatData.FullDataWithTimeZone))}
                    </PageAccountDateContainderStyled>
                    <ImgStyled
                        data-name="account-info-sync-btn"
                        height="32px"
                        src={icons.syncBtnIcon}
                        width="32px"
                        onClick={loadInitData}
                    />
                </PageAccountInfoSyncDateStyled>
            </PageTitle>

            <PageAccountInfoBodyStyled>

                <PageAccountInfoSideStyled>
                    <SideMenu width={285}>
                        {tabs.map(renderSideMenuItem)}
                    </SideMenu>
                </PageAccountInfoSideStyled>

                <NoticeMessage place={ENotificationPlace.accountId}/>

                <PageAccountInfoContentStyled>
                    {
                        isError
                            ? <ErrorPage
                                src={themeIcons.errorDeliveryImage3x}
                                text={translationCommon.error.codes[error?.response?.code ?? '']
                                || translationCommon.error.ui.text}
                                title={translationCommon.error.ui.title}
                            />
                            : !isFetching && clientId && isSuccess && children
                    }
                </PageAccountInfoContentStyled>

            </PageAccountInfoBodyStyled>

            <PageAccountInfoFooter />
        </Loader>

    </PageAccountInfoStyled>;
};

export default memo(PageAccountInfo);
